import React, { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, 
  Camera, 
  Image as ImageIcon, 
  Send, 
  X, 
  Upload,
  Users, 
  Lock, 
  UserCheck, 
  Shield,
  Sparkles,
  Heart,
  Star
} from 'lucide-react';
import { useLanguage } from '../../hooks/useLanguage';
import { useUI } from '../../hooks/useUI';
import { useAuth } from '../../hooks/useAuth';
import { SCREENS } from '../../utils/constants';

/**
 * Image Creation Screen - Futuristic iPhone 17 iOS 26 Design
 * Dopamine-optimized interface for sharing images
 */
const ImageCreationScreen = () => {
  const { t } = useLanguage();
  const { navigateToScreen, screenParams, addFeedItem } = useUI();
  const { user } = useAuth();

  // State management
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [caption, setCaption] = useState('');
  const [selectedPrivacy, setSelectedPrivacy] = useState('circle');
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isPosting, setIsPosting] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  // Refs
  const fileInputRef = useRef(null);
  const dragRef = useRef(null);

  // Privacy options
  const privacyOptions = [
    {
      id: 'circle',
      title: t('privacy.circle', 'Circle'),
      description: t('privacy.circleDesc', 'Tüm circle üyelerine görünür'),
      icon: Users,
      gradient: 'from-green-500 to-emerald-600',
      emoji: '👥'
    },
    {
      id: 'close',
      title: t('privacy.closeCircle', 'Yakın Circle'),
      description: t('privacy.closeDesc', 'Sadece yakın arkadaşlarına görünür'),
      icon: UserCheck,
      gradient: 'from-amber-500 to-orange-600',
      emoji: '❤️'
    },
    {
      id: 'selected',
      title: t('privacy.selected', 'Seçili Kişiler'),
      description: t('privacy.selectedDesc', 'Belirlediğin kişilere görünür'),
      icon: Shield,
      gradient: 'from-blue-500 to-cyan-600',
      emoji: '👤'
    },
    {
      id: 'vault',
      title: t('privacy.vault', 'Dijital Kasa'),
      description: t('privacy.vaultDesc', 'Sadece sana görünür, şifrelenmiş'),
      icon: Lock,
      gradient: 'from-purple-600 to-violet-700',
      emoji: '🔒'
    }
  ];

  // Haptic feedback
  const triggerHaptic = useCallback((type = 'soft') => {
    if (window.navigator && window.navigator.vibrate) {
      const patterns = {
        soft: [10],
        medium: [20],
        strong: [30]
      };
      window.navigator.vibrate(patterns[type] || patterns.soft);
    }
  }, []);

  // Handle file selection
  const handleFileSelect = useCallback((file) => {
    if (file && file.type.startsWith('image/')) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
      triggerHaptic('soft');
    }
  }, [triggerHaptic]);

  // Handle file input change
  const handleFileInputChange = useCallback((event) => {
    const file = event.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  }, [handleFileSelect]);

  // Handle drag events
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  }, [handleFileSelect]);

  // Camera handler
  const handleCamera = useCallback(() => {
    fileInputRef.current?.click();
    triggerHaptic('soft');
  }, [triggerHaptic]);

  // Gallery handler
  const handleGallery = useCallback(() => {
    fileInputRef.current?.click();
    triggerHaptic('soft');
  }, [triggerHaptic]);

  // Remove image
  const removeImage = useCallback(() => {
    setSelectedImage(null);
    setImagePreview(null);
    triggerHaptic('medium');
  }, [triggerHaptic]);

  // Handle back navigation
  const handleBack = useCallback(() => {
    triggerHaptic('soft');
    navigateToScreen(SCREENS.IDENTITY_CIRCLE);
  }, [navigateToScreen, triggerHaptic]);

  // Handle privacy selection
  const handlePrivacySelect = useCallback((privacy) => {
    setSelectedPrivacy(privacy);
    setShowPrivacyModal(false);
    triggerHaptic('soft');
  }, [triggerHaptic]);

  // Handle post
  const handlePost = useCallback(async () => {
    if (!selectedImage || isPosting) return;

    setIsPosting(true);
    triggerHaptic('medium');

    try {
      // Simulate upload
      setIsUploading(true);
      await new Promise(resolve => setTimeout(resolve, 2000));
      setIsUploading(false);      // Create new feed item
      const newPost = {
        type: 'media',
        user: {
          name: user?.name || 'You',
          profilePhoto: user?.profilePhoto || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150&h=150&fit=crop&crop=face&auto=format&q=80',
          avatar: user?.name?.charAt(0) || 'Y',
          verified: user?.verified || false
        },
        content: caption.trim() || 'Shared an image',
        imageUrl: imagePreview,
        timestamp: new Date(),
        privacy: selectedPrivacy,
        likes: 0,
        comments: [],
        shares: 0,
        tags: ['media']
      };
      
      // Add to feed
      addFeedItem(newPost);

      console.log('Posting image:', {
        image: selectedImage.name,
        caption: caption.trim(),
        privacy: selectedPrivacy,
        timestamp: Date.now()
      });

      // Navigate back with success
      navigateToScreen(SCREENS.IDENTITY_CIRCLE);
    } catch (error) {
      console.error('Failed to post image:', error);
      setIsUploading(false);
    } finally {
      setIsPosting(false);
    }
  }, [selectedImage, caption, selectedPrivacy, isPosting, navigateToScreen, triggerHaptic, addFeedItem, user, imagePreview]);

  // Get selected privacy option
  const selectedPrivacyOption = privacyOptions.find(opt => opt.id === selectedPrivacy);
  const SelectedPrivacyIcon = selectedPrivacyOption?.icon;

  // Check if can post
  const canPost = selectedImage && !isPosting && !isUploading;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute inset-0 opacity-20"
          animate={{
            background: [
              'radial-gradient(circle at 20% 50%, rgba(236, 72, 153, 0.3) 0%, transparent 50%)',
              'radial-gradient(circle at 80% 20%, rgba(219, 39, 119, 0.3) 0%, transparent 50%)',
              'radial-gradient(circle at 40% 80%, rgba(244, 63, 94, 0.3) 0%, transparent 50%)'
            ]
          }}
          transition={{ duration: 8, repeat: Infinity, repeatType: 'reverse' }}
        />
        
        {/* Floating decorative elements */}
        {[...Array(5)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute opacity-20"
            animate={{
              x: [0, 100, 0],
              y: [0, -50, 0],
              rotate: [0, 360]
            }}
            transition={{
              duration: 15 + i * 3,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            style={{
              left: Math.random() * 100 + '%',
              top: Math.random() * 100 + '%'
            }}
          >
            {i % 2 === 0 ? (
              <Heart className="w-6 h-6 text-pink-400" />
            ) : (
              <Star className="w-4 h-4 text-rose-400" />
            )}
          </motion.div>
        ))}
      </div>

      {/* Header */}
      <motion.div 
        className="relative z-10 flex items-center justify-between p-6 backdrop-blur-xl bg-slate-800/40 border-b border-white/10"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex items-center space-x-4">
          <motion.button
            className="w-10 h-10 bg-white/10 backdrop-blur-xl rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={handleBack}
          >
            <ArrowLeft className="w-5 h-5" />
          </motion.button>
          
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-rose-500 to-pink-600 rounded-full flex items-center justify-center">
              <ImageIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">
                {t('create.shareImage', 'Görsel Paylaş')}
              </h1>
              <p className="text-sm text-white/60">
                {t('create.captureMemories', 'Anılarını yakala')}
              </p>
            </div>
          </div>
        </div>

        <motion.button
          className={`px-6 py-3 rounded-2xl font-semibold transition-all flex items-center space-x-2 ${
            canPost
              ? 'bg-gradient-to-r from-rose-500 to-pink-600 text-white shadow-lg hover:shadow-rose-500/25'
              : 'bg-white/10 text-white/50 cursor-not-allowed'
          }`}
          whileHover={canPost ? { scale: 1.05, y: -2 } : {}}
          whileTap={canPost ? { scale: 0.95 } : {}}
          onClick={handlePost}
          disabled={!canPost}
        >
          {isPosting || isUploading ? (
            <>
              <motion.div
                className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              />
              <span>
                {isUploading ? t('common.uploading', 'Yükleniyor...') : t('common.posting', 'Paylaşılıyor...')}
              </span>
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              <span>{t('common.share', 'Paylaş')}</span>
            </>
          )}
        </motion.button>
      </motion.div>

      {/* Content Area */}
      <div className="relative z-10 p-6 space-y-6">
        {/* Image Upload Area */}
        <motion.div
          className={`bg-gradient-to-br from-slate-800/60 via-slate-700/40 to-slate-900/60 backdrop-blur-xl rounded-3xl border border-white/10 shadow-xl overflow-hidden ${
            dragActive ? 'border-rose-400 bg-rose-500/10' : ''
          }`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          ref={dragRef}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          {imagePreview ? (
            /* Image Preview */
            <div className="relative">
              <img
                src={imagePreview}
                alt="Preview"
                className="w-full h-80 object-cover"
              />
              
              {/* Overlay with remove button */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent">
                <motion.button
                  className="absolute top-4 right-4 w-10 h-10 bg-black/50 backdrop-blur-xl rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={removeImage}
                >
                  <X className="w-5 h-5" />
                </motion.button>
              </div>

              {/* Upload progress overlay */}
              {isUploading && (
                <motion.div
                  className="absolute inset-0 bg-black/70 flex items-center justify-center"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <div className="text-center">
                    <motion.div
                      className="w-16 h-16 border-4 border-white/30 border-t-white rounded-full mx-auto mb-4"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    />
                    <p className="text-white font-semibold">
                      {t('common.uploading', 'Yükleniyor...')}
                    </p>
                  </div>
                </motion.div>
              )}
            </div>
          ) : (
            /* Upload Area */
            <div className="p-12">
              <div className="text-center">
                <motion.div
                  className="w-24 h-24 bg-gradient-to-br from-rose-500 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-6"
                  whileHover={{ scale: 1.05 }}
                  animate={{ y: [-5, 5, -5] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                >
                  <Upload className="w-12 h-12 text-white" />
                </motion.div>
                
                <h3 className="text-xl font-bold text-white mb-2">
                  {t('image.uploadTitle', 'Görselini Seç')}
                </h3>
                <p className="text-white/60 mb-8">
                  {t('image.uploadDesc', 'Sürükle bırak veya aşağıdaki seçenekleri kullan')}
                </p>

                {/* Action Buttons */}
                <div className="grid grid-cols-2 gap-4">
                  <motion.button
                    className="p-6 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl text-white shadow-xl"
                    whileHover={{ scale: 1.05, y: -3 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleCamera}
                  >
                    <Camera className="w-8 h-8 mx-auto mb-3" />
                    <span className="font-semibold">{t('image.camera', 'Kamera')}</span>
                  </motion.button>
                  
                  <motion.button
                    className="p-6 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl text-white shadow-xl"
                    whileHover={{ scale: 1.05, y: -3 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleGallery}
                  >
                    <ImageIcon className="w-8 h-8 mx-auto mb-3" />
                    <span className="font-semibold">{t('image.gallery', 'Galeri')}</span>
                  </motion.button>
                </div>
              </div>
            </div>
          )}
        </motion.div>

        {/* Caption input */}
        {imagePreview && (
          <motion.div
            className="bg-gradient-to-br from-slate-800/60 via-slate-700/40 to-slate-900/60 backdrop-blur-xl rounded-2xl p-4 border border-white/10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="flex items-start space-x-3">
              <Sparkles className="w-5 h-5 text-rose-400 mt-1" />
              <textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder={t('image.captionPlaceholder', 'Görselini anlat...')}
                className="flex-1 bg-transparent text-white placeholder-white/40 focus:outline-none resize-none"
                rows={3}
                maxLength={300}
              />
            </div>
            <div className="text-right mt-2">
              <span className={`text-xs ${caption.length > 250 ? 'text-red-400' : 'text-white/60'}`}>
                {caption.length}/300
              </span>
            </div>
          </motion.div>
        )}

        {/* Privacy selection */}
        {imagePreview && (
          <motion.div
            className="bg-gradient-to-br from-slate-800/60 via-slate-700/40 to-slate-900/60 backdrop-blur-xl rounded-2xl border border-white/10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <button
              className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors rounded-2xl"
              onClick={() => setShowPrivacyModal(true)}
            >
              <div className="flex items-center space-x-3">
                <div className={`w-10 h-10 bg-gradient-to-r ${selectedPrivacyOption?.gradient} rounded-xl flex items-center justify-center`}>
                  <SelectedPrivacyIcon className="w-5 h-5 text-white" />
                </div>
                <div className="text-left">
                  <h3 className="text-white font-semibold">{selectedPrivacyOption?.title}</h3>
                  <p className="text-white/60 text-sm">{selectedPrivacyOption?.description}</p>
                </div>
              </div>
              <motion.div
                whileHover={{ scale: 1.1 }}
                className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center"
              >
                <span className="text-white/60">›</span>
              </motion.div>
            </button>
          </motion.div>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileInputChange}
        className="hidden"
      />

      {/* Privacy Selection Modal */}
      <AnimatePresence>
        {showPrivacyModal && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowPrivacyModal(false)}
          >
            {/* Backdrop */}
            <motion.div
              className="absolute inset-0 bg-black/70 backdrop-blur-2xl"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />
            
            {/* Modal content */}
            <motion.div
              className="relative w-full max-w-lg bg-gradient-to-br from-slate-800/95 via-slate-700/90 to-slate-900/95 backdrop-blur-3xl rounded-3xl p-8 border border-white/10 shadow-2xl"
              initial={{ scale: 0.8, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 50 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center mb-8">
                <motion.div
                  className="w-16 h-16 bg-gradient-to-br from-rose-500 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4"
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                >
                  <ImageIcon className="w-8 h-8 text-white" />
                </motion.div>
                <h2 className="text-2xl font-bold text-white mb-3">
                  {t('privacy.title', 'Gizlilik Ayarları')}
                </h2>
                <p className="text-white/70">
                  {t('privacy.subtitle', 'Görselini kimler görebilsin?')}
                </p>
              </div>

              <div className="space-y-4">
                {privacyOptions.map((option) => {
                  const IconComponent = option.icon;
                  const isSelected = selectedPrivacy === option.id;
                  return (
                    <motion.button
                      key={option.id}
                      className={`w-full p-5 rounded-2xl bg-gradient-to-r ${option.gradient} flex items-center space-x-4 text-white shadow-xl border-2 transition-all ${
                        isSelected ? 'border-white/40 scale-105' : 'border-white/10'
                      }`}
                      whileHover={{ scale: isSelected ? 1.05 : 1.02, y: -2 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handlePrivacySelect(option.id)}
                    >
                      <div className="w-12 h-12 bg-white/25 rounded-xl flex items-center justify-center">
                        <IconComponent className="w-6 h-6" />
                      </div>
                      <div className="flex-1 text-left">
                        <h3 className="font-bold text-lg">{option.title}</h3>
                        <p className="text-sm text-white/90 mt-1">{option.description}</p>
                      </div>
                      <motion.div
                        className={`w-6 h-6 border-2 border-white/50 rounded-full flex items-center justify-center ${
                          isSelected ? 'bg-white/20' : ''
                        }`}
                        whileHover={{ scale: 1.2 }}
                      >
                        {isSelected && (
                          <motion.div
                            className="w-3 h-3 bg-white rounded-full"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", stiffness: 300 }}
                          />
                        )}
                      </motion.div>
                    </motion.button>
                  );
                })}
              </div>

              {/* Close button */}
              <motion.button
                className="absolute top-4 right-4 w-10 h-10 bg-white/20 backdrop-blur-xl rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                onClick={() => setShowPrivacyModal(false)}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <X className="w-5 h-5" />
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ImageCreationScreen;
